<script setup lang="ts">
import {
  PhArrowLeft as ArrowLeftIcon,
  PhArrowRight as ArrowRightIcon,
  PhCheckCircle as CheckCircleIcon,
  PhInfo as InfoIcon,
  PhImage as PictureIcon,
  PhPalette as PaletteIcon,
  PhRuler as RulerIcon,
  PhShieldCheck as ShieldCheckIcon,
  PhWarningCircle as WarningCircleIcon,
  PhWrench as WrenchIcon,
  PhX as CloseIcon,
} from '@phosphor-icons/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProductImage from '../components/ProductImage.vue'
import StatusState from '../components/StatusState.vue'
import { ApiError, api } from '../services/api'
import { useCustomizationStore } from '../stores/customization'
import { useSessionStore } from '../stores/session'
import type {
  ConfigurationValidation,
  InquiryPayload,
  Product,
  ProductConfiguration,
  ProductSku,
  SpecificationPart,
} from '../types'
import { hardwareComponentKey, hardwareComponentsMatch } from '../utils/hardware'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const customization = useCustomizationStore()
const product = ref<Product | null>(null)
const loading = ref(true)
const error = ref('')
const validating = ref(false)
const validation = ref<ConfigurationValidation | null>(null)
const inquiryOpen = ref(false)
const lightboxOpen = ref(false)
const lightboxImageUrl = ref('')
const lightboxImageAlt = ref('')
const profileAttempted = ref(false)
const submitting = ref(false)
const submitError = ref('')
const submittedNo = ref('')
const activeImageUrl = ref('')
const mainImageTriggerRef = ref<HTMLButtonElement | null>(null)
const lightboxCloseRef = ref<HTMLButtonElement | null>(null)
const hardwareComparisonScrollRef = ref<HTMLElement | null>(null)
const hardwareComparisonScrollable = ref(false)
let validationRequestSequence = 0

const config = reactive<ProductConfiguration>({
  productId: '',
  productVersionId: '',
  colorCode: '',
  partColors: {},
  hardwareCode: '',
  specificationId: '',
  skuCode: '',
  widthMm: 0,
  heightMm: 0,
  depthMm: 0,
})
const dimensionMode = ref<'fixed' | 'custom'>('fixed')
const selectedSpecificationKey = ref('')

const inquiry = reactive({
  contactName: '',
  wechatId: '',
  notes: '',
})

const selectedColor = computed(() =>
  product.value?.colors.find((item) => item.code === config.colorCode),
)
const selectedHardware = computed(() =>
  product.value?.hardwarePackages.find((item) => item.code === config.hardwareCode),
)
const selectedSku = computed(() =>
  product.value?.skus.find((item) => item.code === config.skuCode),
)
const customizationProfile = computed(() => product.value?.customizationProfile)
const hasCustomizationProfile = computed(() => Boolean(customizationProfile.value))
const customizationParts = computed(() =>
  customizationProfile.value?.specification.parts || [],
)
type ProductSpecificationView = {
  id: string
  apiId: string
  name: string
  parts: SpecificationPart[]
  purchaseMode: 'standard' | 'custom'
  purchaseModeExplicit: boolean
}

const specifications = computed<ProductSpecificationView[]>(() => {
  if (product.value?.specifications.length) {
    return product.value.specifications.map((specification) => ({
      id: specification.id,
      apiId: specification.id,
      name: specification.name,
      parts: specification.parts,
      purchaseMode: specification.purchaseMode === 'custom' ? 'custom' : 'standard',
      purchaseModeExplicit: specification.purchaseModeExplicit === true,
    }))
  }

  const items = new Map<
    string,
    ProductSpecificationView
  >()
  product.value?.skus.forEach((sku) => {
    const key = sku.specificationId || 'legacy-default'
    if (!items.has(key)) {
      items.set(key, {
        id: key,
        apiId: sku.specificationId,
        name: sku.specificationName,
        parts: sku.specificationParts,
        purchaseMode: sku.purchaseMode === 'custom' ? 'custom' : 'standard',
        purchaseModeExplicit: sku.purchaseModeExplicit === true,
      })
    }
  })
  return [...items.values()]
})
const selectedSpecification = computed(() =>
  specifications.value.find((item) => item.id === selectedSpecificationKey.value),
)
const explicitPurchaseMode = computed(
  () => selectedSpecification.value?.purchaseModeExplicit === true,
)
const explicitStandardPurchaseMode = computed(
  () => explicitPurchaseMode.value && selectedSpecification.value?.purchaseMode === 'standard',
)
const explicitCustomPurchaseMode = computed(
  () => explicitPurchaseMode.value && selectedSpecification.value?.purchaseMode === 'custom',
)
const legacyPurchaseMode = computed(() => !explicitPurchaseMode.value)
const selectedSpecificationParts = computed(() => selectedSpecification.value?.parts || [])
const specificationSkus = computed(() =>
  product.value?.skus.filter(
    (sku) => (sku.specificationId || 'legacy-default') === selectedSpecificationKey.value,
  ) || [],
)
const hasRequiredStandardSku = computed(
  () => hasCustomizationProfile.value
    || !explicitStandardPurchaseMode.value
    || specificationSkus.value.some((sku) => sku.code === config.skuCode),
)

function skusForSpecification(specificationId: string) {
  return product.value?.skus.filter(
    (sku) => (sku.specificationId || 'legacy-default') === specificationId,
  ) || []
}

function specificationSizeSummary(specification: ProductSpecificationView) {
  const count = skusForSpecification(specification.id).length
  if (!specification.purchaseModeExplicit) return `${count} 个标准尺寸`
  if (specification.purchaseMode === 'custom') {
    return count ? `${count} 个推荐尺寸` : '支持自由填写尺寸'
  }
  return count ? `${count} 个标准尺寸` : '暂无可选标准尺寸'
}

function colorsForPart(part: SpecificationPart) {
  if (!product.value) return []
  if (!part.allowedColorCodes.length) return product.value.colors
  const allowedCodes = new Set(part.allowedColorCodes)
  return product.value.colors.filter((color) => allowedCodes.has(color.code))
}

function resetPartColorsForSpecification(
  specification: (typeof specifications.value)[number] | undefined,
) {
  const nextPartColors: Record<string, string> = {}
  specification?.parts.forEach((part) => {
    const colorOptions = colorsForPart(part)
    const defaultIsAllowed = colorOptions.some((color) => color.code === part.defaultColorCode)
    nextPartColors[part.id] = defaultIsAllowed
      ? part.defaultColorCode
      : colorOptions[0]?.code || ''
  })
  config.partColors = nextPartColors
}

const selectedPartColorSummary = computed(() =>
  selectedSpecificationParts.value
    .map((part) => {
      const color = product.value?.colors.find(
        (item) => item.code === config.partColors[part.id],
      )
      return color ? `${part.name}：${color.name}` : ''
    })
    .filter(Boolean)
    .join(' / '),
)

const selectedCustomizationColorSummary = computed(() =>
  customizationParts.value
    .map((part) => {
      const color = product.value?.colors.find(
        (item) => item.code === config.partColors[part.id],
      )
      return color ? `${part.name}：${color.name}` : ''
    })
    .filter(Boolean)
    .join(' / '),
)

function selectedColorForPart(part: SpecificationPart) {
  return product.value?.colors.find((color) => color.code === config.partColors[part.id])
}

function hardwareRequirementStats(requirements: ProductSku['hardwareRequirements']) {
  const activeRequirements = requirements.filter((item) => item.quantity > 0)
  return {
    typeCount: activeRequirements.length,
    totalQuantity: activeRequirements.reduce((total, item) => total + item.quantity, 0),
  }
}

function hardwareRequirementSummary(requirements: ProductSku['hardwareRequirements']) {
  const stats = hardwareRequirementStats(requirements)
  return stats.typeCount ? `${stats.typeCount} 类组件，共 ${stats.totalQuantity} 件` : ''
}

const selectedHardwareRequirements = computed(() =>
  (selectedSku.value?.hardwareRequirements || []).filter((item) => item.quantity > 0),
)
const selectedHardwareRequirementSummary = computed(() =>
  hardwareRequirementSummary(selectedHardwareRequirements.value),
)

const hardwareComparisonRows = computed(() => {
  const packages = product.value?.hardwarePackages || []
  if (selectedHardwareRequirements.value.length) {
    return selectedHardwareRequirements.value.map((requirement) => ({
      key: hardwareComponentKey(requirement.componentKey, requirement.label),
      label: requirement.label,
      quantity: requirement.quantity,
      cells: packages.map((hardware) => {
        const item = hardware.specifications.find((specification) =>
          hardwareComponentsMatch(requirement, specification),
        )
        return {
          code: hardware.code,
          value: item ? item.value || '已包含' : '不含',
          note: item?.note || '',
        }
      }),
    }))
  }

  const labels = new Map<string, { label: string; componentKey: string }>()
  packages.forEach((hardware) => {
    hardware.specifications.forEach((item) => {
      const key = hardwareComponentKey(item.componentKey, item.label)
      if (key !== 'label:' && !labels.has(key)) {
        labels.set(key, {
          label: item.label.trim(),
          componentKey: item.componentKey,
        })
      }
    })
  })
  return [...labels.entries()].map(([key, definition]) => ({
    key,
    label: definition.label,
    quantity: undefined,
    cells: packages.map((hardware) => {
      const item = hardware.specifications.find(
        (specification) =>
          hardwareComponentKey(specification.componentKey, specification.label) === key,
      )
      return {
        code: hardware.code,
        value: item ? item.value || '已包含' : '不含',
        note: item?.note || '',
      }
    }),
  }))
})

let hardwareComparisonResizeObserver: ResizeObserver | null = null

function updateHardwareComparisonScrollability() {
  const element = hardwareComparisonScrollRef.value
  hardwareComparisonScrollable.value = Boolean(
    element && element.scrollWidth > element.clientWidth + 1,
  )
}

function observeHardwareComparison(element: HTMLElement | null) {
  hardwareComparisonResizeObserver?.disconnect()
  hardwareComparisonResizeObserver = null
  hardwareComparisonScrollable.value = false
  if (!element) return

  nextTick(() => {
    updateHardwareComparisonScrollability()
    if (typeof ResizeObserver !== 'undefined') {
      hardwareComparisonResizeObserver = new ResizeObserver(
        updateHardwareComparisonScrollability,
      )
      hardwareComparisonResizeObserver.observe(element)
    }
  })
}

const configurationSummary = computed(() => {
  if (!product.value) return ''
  const items = []
  if (hasCustomizationProfile.value) {
    if (selectedCustomizationColorSummary.value) items.push(selectedCustomizationColorSummary.value)
  } else if (selectedSku.value) {
    items.push(`${selectedSku.value.specificationName} / ${selectedSku.value.sizeName}`)
  } else if (selectedSpecification.value) {
    items.push(`${selectedSpecification.value.name} / 定制尺寸`)
  }
  if (!hasCustomizationProfile.value && selectedSpecificationParts.value.length) {
    if (selectedPartColorSummary.value) items.push(selectedPartColorSummary.value)
  } else if (product.value.colors.length && selectedColor.value) {
    items.push(selectedColor.value.name)
  }
  if (config.widthMm > 0 && config.heightMm > 0 && config.depthMm > 0) {
    items.push(
      `${product.value.dimensionRanges.widthMm.label || '宽度'} ${millimetersToCentimeters(config.widthMm)}、`
        + `${product.value.dimensionRanges.heightMm.label || '高度'} ${millimetersToCentimeters(config.heightMm)}、`
        + `${product.value.dimensionRanges.depthMm.label || '深度'} ${millimetersToCentimeters(config.depthMm)} 厘米`,
    )
  } else if (hasCustomizationProfile.value) {
    items.push('尺寸待填写')
  }
  if (product.value.hardwarePackages.length && selectedHardware.value) {
    items.push(
      selectedHardwareRequirementSummary.value
        ? `${selectedHardware.value.name}（${selectedHardwareRequirementSummary.value}）`
        : selectedHardware.value.name,
    )
  }
  return items.join('，')
})

function applyProductDefaults(value: Product) {
  if (value.customizationProfile) {
    const restored = customization.configurationFor(value)
    Object.assign(config, restored, { partColors: { ...restored.partColors } })
    selectedSpecificationKey.value = value.customizationProfile.specificationId
    dimensionMode.value = 'custom'
    activeImageUrl.value = value.image
    validation.value = null
    return
  }
  config.productId = value.id
  config.productVersionId = value.versionId
  config.colorCode = value.colors[0]?.code || ''
  config.partColors = {}
  config.hardwareCode = value.hardwarePackages.find((item) => item.isDefault)?.code
    || value.hardwarePackages[0]?.code
    || ''
  selectedSpecificationKey.value = ''
  config.specificationId = ''
  config.skuCode = ''
  dimensionMode.value = value.skus.length ? 'fixed' : 'custom'
  config.widthMm = value.dimensionRanges.widthMm.default
  config.heightMm = value.dimensionRanges.heightMm.default
  config.depthMm = value.dimensionRanges.depthMm.default
  activeImageUrl.value = value.image
  const firstSpecification = specifications.value[0]
  if (firstSpecification?.purchaseModeExplicit) {
    selectSpecification(firstSpecification.id)
  } else if (value.skus[0]) {
    selectSku(value.skus[0].code)
  }
}

async function loadProduct() {
  loading.value = true
  error.value = ''
  try {
    const value = await api.getProduct(String(route.params.slug))
    product.value = value
    applyProductDefaults(value)
    await validate()
    if (route.hash) {
      await nextTick()
      document.getElementById(route.hash.slice(1))?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '产品加载失败'
  } finally {
    loading.value = false
  }
}

async function validate() {
  if (!config.productId) return
  const requestSequence = ++validationRequestSequence
  if (hasCustomizationProfile.value && product.value) {
    const localMessages: ConfigurationValidation['messages'] = []
    customizationParts.value.forEach((part) => {
      const selected = String(config.partColors[part.id] || '')
      const allowed = colorsForPart(part)
      if (!selected && part.required) {
        localMessages.push({
          level: 'error',
          field: `partColors.${part.id}`,
          message: `请选择${part.name}。`,
        })
      } else if (selected && !allowed.some((color) => color.code === selected)) {
        localMessages.push({
          level: 'error',
          field: `partColors.${part.id}`,
          message: `${part.name}所选颜色已不可用，请重新选择。`,
        })
      }
    })
    ;([
      ['widthMm', product.value.dimensionRanges.widthMm.label || '宽度'],
      ['heightMm', product.value.dimensionRanges.heightMm.label || '高度'],
      ['depthMm', product.value.dimensionRanges.depthMm.label || '深度'],
    ] as const).forEach(([field, label]) => {
      if (!Number.isFinite(config[field]) || config[field] <= 0) {
        localMessages.push({ level: 'error', field, message: `请填写${label}。` })
      }
    })
    if (product.value.hardwarePackages.length && !selectedHardware.value) {
      localMessages.push({ level: 'error', field: 'hardwareCode', message: '请选择五金方案。' })
    }
    if (localMessages.length) {
      validating.value = false
      validation.value = {
        valid: false,
        requiresManualReview: false,
        messages: localMessages,
      }
      return
    }
  }
  if (!hasRequiredStandardSku.value) {
    validating.value = false
    validation.value = {
      valid: false,
      requiresManualReview: false,
      messages: [{ level: 'error', field: 'sku_code', message: '请选择一个标准尺寸。' }],
    }
    return
  }
  validating.value = true
  const configuration = {
    ...config,
    partColors: { ...config.partColors },
  }
  try {
    const result = await api.validateConfiguration(configuration)
    if (requestSequence !== validationRequestSequence) return
    validation.value = result
  } catch (cause) {
    if (requestSequence !== validationRequestSequence) return
    validation.value = {
      valid: false,
      requiresManualReview: false,
      messages: [
        {
          level: 'error',
          message: cause instanceof Error ? cause.message : '配置校验失败，请重试。',
        },
      ],
    }
  } finally {
    if (requestSequence === validationRequestSequence) validating.value = false
  }
}

function selectSku(code: string) {
  if (!product.value) return
  const sku = product.value.skus.find((item) => item.code === code)
  if (!sku) return
  dimensionMode.value = 'fixed'
  const nextSpecificationKey = sku.specificationId || 'legacy-default'
  const specificationChanged = selectedSpecificationKey.value !== nextSpecificationKey
  selectedSpecificationKey.value = nextSpecificationKey
  config.specificationId = sku.specificationId
  config.skuCode = sku.code
  if (sku.dimensions.widthMm > 0) config.widthMm = sku.dimensions.widthMm
  if (sku.dimensions.heightMm > 0) config.heightMm = sku.dimensions.heightMm
  if (sku.dimensions.depthMm > 0) config.depthMm = sku.dimensions.depthMm
  activeImageUrl.value = sku.imageEnabled && sku.image ? sku.image : product.value.image
  if (specificationChanged) {
    resetPartColorsForSpecification(
      specifications.value.find((item) => item.id === nextSpecificationKey),
    )
  }
}

function selectSpecification(specificationKey: string) {
  const specification = specifications.value.find((item) => item.id === specificationKey)
  const specificationChanged = selectedSpecificationKey.value !== specificationKey
  selectedSpecificationKey.value = specificationKey
  config.specificationId = specification?.apiId || ''
  if (specificationChanged) resetPartColorsForSpecification(specification)
  if (specification?.purchaseModeExplicit) {
    if (specificationChanged) {
      validation.value = null
      config.skuCode = ''
      config.widthMm = product.value?.dimensionRanges.widthMm.default || 0
      config.heightMm = product.value?.dimensionRanges.heightMm.default || 0
      config.depthMm = product.value?.dimensionRanges.depthMm.default || 0
      activeImageUrl.value = product.value?.image || ''
      dimensionMode.value = specification.purchaseMode === 'standard' ? 'fixed' : 'custom'
      if (specification.purchaseMode === 'standard') {
        const first = skusForSpecification(specificationKey)[0]
        if (first) selectSku(first.code)
      }
    }
    return
  }
  if (dimensionMode.value === 'fixed') {
    const first = skusForSpecification(specificationKey)[0]
    if (first) selectSku(first.code)
  }
}

function enableCustomDimensions() {
  if (explicitStandardPurchaseMode.value) return
  dimensionMode.value = 'custom'
  config.skuCode = ''
  if (product.value) activeImageUrl.value = product.value.image
}

type DimensionKey = 'widthMm' | 'heightMm' | 'depthMm'

function millimetersToCentimeters(value: number) {
  return value / 10
}

function updateCustomDimension(key: DimensionKey, event: Event) {
  if (
    explicitCustomPurchaseMode.value
    && (dimensionMode.value !== 'custom' || Boolean(config.skuCode))
  ) {
    enableCustomDimensions()
  }
  const rawValue = (event.target as HTMLInputElement).value
  const centimeters = Number(rawValue)
  config[key] = rawValue && Number.isFinite(centimeters)
    ? Math.round(centimeters * 10)
    : 0
}

function dimensionPlaceholder(defaultMillimeters: number) {
  return `如：${millimetersToCentimeters(defaultMillimeters)}（单位：cm）`
}

function returnToFixedSize() {
  const first = specificationSkus.value[0]
  if (first) selectSku(first.code)
}

function selectSkuFromChange(code: string) {
  if (config.skuCode !== code) selectSku(code)
}

function selectGalleryImage(url: string) {
  activeImageUrl.value = url
}

function openLightbox(url = activeImageUrl.value || product.value?.image || '', alt = '') {
  lightboxImageUrl.value = url
  lightboxImageAlt.value = alt || `${product.value?.name || '商品'}大图`
  lightboxOpen.value = true
  nextTick(() => lightboxCloseRef.value?.focus())
}

function closeLightbox() {
  if (!lightboxOpen.value) return
  lightboxOpen.value = false
  nextTick(() => mainImageTriggerRef.value?.focus())
}

function closeInquiry() {
  inquiryOpen.value = false
}

function handleOverlayKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  if (lightboxOpen.value) {
    closeLightbox()
    return
  }
  if (inquiryOpen.value) closeInquiry()
}

async function startInquiry() {
  profileAttempted.value = true
  await validate()
  if (!validation.value?.valid) return
  if (hasCustomizationProfile.value && product.value) {
    customization.save(product.value, { ...config, partColors: { ...config.partColors } })
    await router.push({
      name: 'customization-review',
      params: { slug: product.value.slug },
    })
    return
  }
  if (!session.isAuthenticated) {
    sessionStorage.setItem(
      'cabinet-pending-config',
      JSON.stringify({ slug: route.params.slug, configuration: { ...config } }),
    )
    await router.push({ name: 'login', query: { redirect: route.fullPath } })
    return
  }
  inquiryOpen.value = true
}

async function submitInquiry() {
  if (!product.value || submitting.value) return
  submitting.value = true
  submitError.value = ''
  try {
    const payload: InquiryPayload = {
      configuration: { ...config },
      contactName: inquiry.contactName.trim(),
      notes: [
        inquiry.wechatId.trim() ? `微信：${inquiry.wechatId.trim()}` : '',
        inquiry.notes.trim(),
      ].filter(Boolean).join('；') || undefined,
    }
    const result = await api.createInquiry(payload)
    submittedNo.value = result.inquiryNo
  } catch (cause) {
    if (cause instanceof ApiError) submitError.value = cause.message
    else submitError.value = cause instanceof Error ? cause.message : '询价提交失败'
  } finally {
    submitting.value = false
  }
}

let validateTimer = 0
watch(
  () => [
    config.colorCode,
    JSON.stringify(config.partColors),
    config.hardwareCode,
    config.specificationId,
    config.skuCode,
    config.widthMm,
    config.heightMm,
    config.depthMm,
  ],
  () => {
    if (hasCustomizationProfile.value && product.value) {
      customization.save(product.value, { ...config, partColors: { ...config.partColors } })
    }
    window.clearTimeout(validateTimer)
    validationRequestSequence += 1
    validating.value = false
    if (!hasCustomizationProfile.value || profileAttempted.value) {
      validateTimer = window.setTimeout(validate, 350)
    }
  },
)

watch(hardwareComparisonScrollRef, observeHardwareComparison, { flush: 'post' })
watch(
  () => [hardwareComparisonRows.value.length, product.value?.hardwarePackages.length],
  () => nextTick(updateHardwareComparisonScrollability),
)
watch(
  () => inquiryOpen.value || lightboxOpen.value,
  (overlayOpen) => document.body.classList.toggle('has-customer-overlay', overlayOpen),
)

onMounted(() => {
  document.body.classList.add('has-product-config-bar')
  window.addEventListener('resize', updateHardwareComparisonScrollability)
  window.addEventListener('keydown', handleOverlayKeydown)
  loadProduct()
})

onBeforeUnmount(() => {
  document.body.classList.remove('has-product-config-bar')
  document.body.classList.remove('has-customer-overlay')
  validationRequestSequence += 1
  window.clearTimeout(validateTimer)
  window.removeEventListener('resize', updateHardwareComparisonScrollability)
  window.removeEventListener('keydown', handleOverlayKeydown)
  hardwareComparisonResizeObserver?.disconnect()
})
</script>

<template>
  <section class="section section--product">
    <div class="container">
      <RouterLink class="back-link" to="/products">
        <ArrowLeftIcon :size="18" aria-hidden="true" />
        返回产品中心
      </RouterLink>

      <StatusState v-if="loading" kind="loading" />
      <StatusState
        v-else-if="error"
        kind="error"
        title="产品暂时无法显示"
        :message="error"
        @retry="loadProduct"
      />
      <div v-else-if="product" class="product-detail">
        <div class="product-detail__media">
          <button
            ref="mainImageTriggerRef"
            class="product-detail__media-trigger"
            type="button"
            aria-label="点击查看大图"
            aria-haspopup="dialog"
            :aria-expanded="lightboxOpen"
            @click="openLightbox()"
          >
            <ProductImage
              :src="activeImageUrl || product.image"
              :alt="`${product.name}柜体展示图`"
              :position="product.imagePosition"
              eager
            />
          </button>
          <div
            v-if="product.galleryImages.length > 1"
            class="product-gallery"
            role="group"
            aria-label="商品图片缩略图"
          >
            <button
              v-for="image in product.galleryImages"
              :key="image.id"
              type="button"
              :class="{ active: (activeImageUrl || product.image) === image.url }"
              :aria-pressed="(activeImageUrl || product.image) === image.url"
              :aria-label="`查看${image.name}`"
              @click="selectGalleryImage(image.url)"
            >
              <img :src="image.url" :alt="image.name" />
            </button>
          </div>
          <div class="product-notice">
            <InfoIcon :size="20" weight="fill" aria-hidden="true" />
            <p>产品图用于展示固定结构。屏幕色彩与实体小样可能存在差异，最终以花色编号和确认资料为准。</p>
          </div>
        </div>

        <div class="product-config">
          <div class="product-config__heading">
            <span class="category-label">
              {{ product.secondaryCategory ? `${product.category} / ${product.secondaryCategory}` : product.category }}
            </span>
            <h1>{{ product.name }}</h1>
            <p>{{ product.description }}</p>
          </div>

          <fieldset
            v-if="hasCustomizationProfile && product.colors.length"
            id="available-colors"
            class="config-group config-group--available-colors"
          >
            <legend><PaletteIcon :size="20" />可选颜色</legend>
            <p class="profile-section-intro">下方各部位只会显示当前商品可以落地的颜色。</p>
            <ul class="available-color-list" aria-label="当前商品可选颜色">
              <li v-for="color in product.colors" :key="color.id">
                <span class="color-option__swatch" :style="{ background: color.swatch }">
                  <img v-if="color.swatchImage" :src="color.swatchImage" :alt="`${color.name}色卡`" />
                </span>
                <span>
                  <strong>{{ color.name }}</strong>
                  <small>{{ color.supplierCode || color.code }}</small>
                </span>
              </li>
            </ul>
          </fieldset>

          <fieldset
            v-if="hasCustomizationProfile"
            id="custom-colors"
            class="config-group config-group--profile-colors"
          >
            <legend><PaletteIcon :size="20" />第一步：选择定制颜色</legend>
            <p class="profile-section-intro">每个部位都可以独立选色，可选范围由当前商品方案限定。</p>
            <div class="profile-color-fields">
              <label v-for="part in customizationParts" :key="part.id" class="profile-color-field">
                <span class="profile-color-field__label">
                  <strong>{{ part.name }}</strong>
                  <small>{{ part.required ? '必选' : '选填' }}</small>
                </span>
                <span class="profile-color-select">
                  <span
                    v-if="selectedColorForPart(part)"
                    class="profile-color-select__swatch"
                    :style="{ background: selectedColorForPart(part)?.swatch }"
                    aria-hidden="true"
                  >
                    <img
                      v-if="selectedColorForPart(part)?.swatchImage"
                      :src="selectedColorForPart(part)?.swatchImage"
                      alt=""
                    />
                  </span>
                  <select
                    v-model="config.partColors[part.id]"
                    :required="part.required"
                    :aria-label="`选择${part.name}`"
                  >
                    <option v-if="!part.required" value="">不选择</option>
                    <option
                      v-for="color in colorsForPart(part)"
                      :key="`${part.id}-${color.code}`"
                      :value="color.code"
                    >
                      {{ color.name }}（{{ color.supplierCode || color.code }}）
                    </option>
                  </select>
                </span>
              </label>
            </div>
          </fieldset>

          <fieldset
            v-if="hasCustomizationProfile"
            id="custom-size"
            class="config-group config-group--profile-size"
          >
            <legend><RulerIcon :size="20" />第二步：选择定制尺寸</legend>
            <p class="profile-section-intro">请参照示意图填写成品宽、高、深，页面以厘米展示，提交时自动换算为毫米。</p>
            <button
              v-if="product.dimensionReferenceImage"
              class="dimension-reference"
              type="button"
              aria-label="放大查看尺寸参考图"
              @click="openLightbox(product.dimensionReferenceImage.url, product.dimensionReferenceImage.alt)"
            >
              <img
                :src="product.dimensionReferenceImage.url"
                :alt="product.dimensionReferenceImage.alt"
              />
              <span>
                <strong>{{ product.dimensionReferenceImage.name }}</strong>
                <small>点击放大查看</small>
              </span>
            </button>
            <div class="dimension-fields dimension-fields--profile">
              <label>
                <span>{{ product.dimensionRanges.widthMm.label || '宽度' }}（cm／厘米）</span>
                <input
                  :value="config.widthMm > 0 ? millimetersToCentimeters(config.widthMm) : ''"
                  type="number"
                  inputmode="decimal"
                  :min="millimetersToCentimeters(product.dimensionRanges.widthMm.min)"
                  :max="millimetersToCentimeters(product.dimensionRanges.widthMm.max)"
                  :step="millimetersToCentimeters(product.dimensionRanges.widthMm.step)"
                  :placeholder="dimensionPlaceholder(product.dimensionRanges.widthMm.default)"
                  @input="updateCustomDimension('widthMm', $event)"
                />
                <small>可填 {{ millimetersToCentimeters(product.dimensionRanges.widthMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.widthMm.max) }} cm</small>
              </label>
              <label>
                <span>{{ product.dimensionRanges.heightMm.label || '高度' }}（cm／厘米）</span>
                <input
                  :value="config.heightMm > 0 ? millimetersToCentimeters(config.heightMm) : ''"
                  type="number"
                  inputmode="decimal"
                  :min="millimetersToCentimeters(product.dimensionRanges.heightMm.min)"
                  :max="millimetersToCentimeters(product.dimensionRanges.heightMm.max)"
                  :step="millimetersToCentimeters(product.dimensionRanges.heightMm.step)"
                  :placeholder="dimensionPlaceholder(product.dimensionRanges.heightMm.default)"
                  @input="updateCustomDimension('heightMm', $event)"
                />
                <small>可填 {{ millimetersToCentimeters(product.dimensionRanges.heightMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.heightMm.max) }} cm</small>
              </label>
              <label>
                <span>{{ product.dimensionRanges.depthMm.label || '深度' }}（cm／厘米）</span>
                <input
                  :value="config.depthMm > 0 ? millimetersToCentimeters(config.depthMm) : ''"
                  type="number"
                  inputmode="decimal"
                  :min="millimetersToCentimeters(product.dimensionRanges.depthMm.min)"
                  :max="millimetersToCentimeters(product.dimensionRanges.depthMm.max)"
                  :step="millimetersToCentimeters(product.dimensionRanges.depthMm.step)"
                  :placeholder="dimensionPlaceholder(product.dimensionRanges.depthMm.default)"
                  @input="updateCustomDimension('depthMm', $event)"
                />
                <small>可填 {{ millimetersToCentimeters(product.dimensionRanges.depthMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.depthMm.max) }} cm</small>
              </label>
            </div>
          </fieldset>

          <fieldset v-if="!hasCustomizationProfile && specifications.length" class="config-group">
            <legend><PictureIcon :size="20" />第一步：选择规格</legend>
            <div class="specification-options">
              <button
                v-for="specification in specifications"
                :key="specification.id"
                type="button"
                :class="{ active: selectedSpecificationKey === specification.id }"
                :aria-pressed="selectedSpecificationKey === specification.id"
                @click="selectSpecification(specification.id)"
              >
                <strong>{{ specification.name }}</strong>
                <span>{{ specificationSizeSummary(specification) }}</span>
                <CheckCircleIcon :size="20" weight="fill" aria-hidden="true" />
              </button>
            </div>
          </fieldset>

          <fieldset v-if="!hasCustomizationProfile && (explicitPurchaseMode || specificationSkus.length)" class="config-group">
            <legend>
              <RulerIcon :size="20" />
              {{ explicitCustomPurchaseMode ? '第二步：选择尺寸' : '第二步：选择标准尺寸' }}
            </legend>
            <p v-if="explicitCustomPurchaseMode" class="purchase-size-intro">
              可以选择一个推荐尺寸快速填入，也可直接在下方填写允许范围内的宽、高、深。
            </p>
            <div
              v-if="explicitCustomPurchaseMode && specificationSkus.length"
              class="size-section-heading"
            >
              <strong>推荐尺寸</strong>
              <span>点击即可带入对应尺寸</span>
            </div>
            <div v-if="specificationSkus.length" class="sku-options">
              <label v-for="sku in specificationSkus" :key="sku.id">
                <input
                  :checked="config.skuCode === sku.code"
                  type="radio"
                  name="sale-size"
                  :value="sku.code"
                  @click="selectSku(sku.code)"
                  @change="selectSkuFromChange(sku.code)"
                />
                <img
                  :src="sku.imageEnabled && sku.image ? sku.image : product.image"
                  :alt="sku.imageEnabled && sku.image ? `${sku.name} SKU 图片` : `${product.name}商品主图`"
                />
                <span>
                  <strong>{{ sku.sizeName }}</strong>
                  <small>{{ sku.code }}</small>
                  <small>
                    {{ sku.dimensions.widthLabel || '宽' }} {{ sku.dimensions.widthMm }} ·
                    {{ sku.dimensions.heightLabel || '高' }} {{ sku.dimensions.heightMm }} ·
                    {{ sku.dimensions.depthLabel || '深' }} {{ sku.dimensions.depthMm }} mm
                  </small>
                  <small v-if="hardwareRequirementSummary(sku.hardwareRequirements)">
                    五金 {{ hardwareRequirementSummary(sku.hardwareRequirements) }}
                  </small>
                </span>
                <CheckCircleIcon :size="20" weight="fill" aria-hidden="true" />
              </label>
            </div>
            <p v-else-if="explicitStandardPurchaseMode" class="size-options-empty" role="status">
              该规格暂无可选标准尺寸，请联系客服。
            </p>
            <p v-else-if="explicitCustomPurchaseMode" class="size-options-empty">
              该规格暂无推荐尺寸，仍可直接填写自由尺寸。
            </p>
            <dl v-if="selectedSku?.parameters.length" class="combination-parameters">
              <div v-for="parameter in selectedSku.parameters" :key="parameter.id">
                <dt>{{ parameter.label }}</dt>
                <dd>{{ parameter.value }}</dd>
              </div>
            </dl>
            <div
              v-if="explicitCustomPurchaseMode"
              class="custom-size-editor"
              :class="{ 'custom-size-editor--active': dimensionMode === 'custom' }"
            >
              <div class="custom-size-editor__heading">
                <div>
                  <strong>自由填写尺寸</strong>
                  <span>点击任一输入框后，将改为自由尺寸</span>
                </div>
                <em>
                  {{ dimensionMode === 'fixed' && selectedSku
                    ? `当前使用：${selectedSku.sizeName}`
                    : '当前使用：自由尺寸' }}
                </em>
              </div>
              <div class="dimension-fields">
                <label>
                  <span>{{ product.dimensionRanges.widthMm.label || '宽度' }}（cm／厘米）</span>
                  <input
                    :value="millimetersToCentimeters(config.widthMm)"
                    type="number"
                    inputmode="decimal"
                    :min="millimetersToCentimeters(product.dimensionRanges.widthMm.min)"
                    :max="millimetersToCentimeters(product.dimensionRanges.widthMm.max)"
                    :step="millimetersToCentimeters(product.dimensionRanges.widthMm.step)"
                    :placeholder="dimensionPlaceholder(product.dimensionRanges.widthMm.default)"
                    @focus="enableCustomDimensions"
                    @input="updateCustomDimension('widthMm', $event)"
                  />
                  <small>
                    范围 {{ millimetersToCentimeters(product.dimensionRanges.widthMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.widthMm.max) }} cm，
                    每次增减 {{ millimetersToCentimeters(product.dimensionRanges.widthMm.step) }} cm
                  </small>
                </label>
                <label>
                  <span>{{ product.dimensionRanges.heightMm.label || '高度' }}（cm／厘米）</span>
                  <input
                    :value="millimetersToCentimeters(config.heightMm)"
                    type="number"
                    inputmode="decimal"
                    :min="millimetersToCentimeters(product.dimensionRanges.heightMm.min)"
                    :max="millimetersToCentimeters(product.dimensionRanges.heightMm.max)"
                    :step="millimetersToCentimeters(product.dimensionRanges.heightMm.step)"
                    :placeholder="dimensionPlaceholder(product.dimensionRanges.heightMm.default)"
                    @focus="enableCustomDimensions"
                    @input="updateCustomDimension('heightMm', $event)"
                  />
                  <small>
                    范围 {{ millimetersToCentimeters(product.dimensionRanges.heightMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.heightMm.max) }} cm，
                    每次增减 {{ millimetersToCentimeters(product.dimensionRanges.heightMm.step) }} cm
                  </small>
                </label>
                <label>
                  <span>{{ product.dimensionRanges.depthMm.label || '深度' }}（cm／厘米）</span>
                  <input
                    :value="millimetersToCentimeters(config.depthMm)"
                    type="number"
                    inputmode="decimal"
                    :min="millimetersToCentimeters(product.dimensionRanges.depthMm.min)"
                    :max="millimetersToCentimeters(product.dimensionRanges.depthMm.max)"
                    :step="millimetersToCentimeters(product.dimensionRanges.depthMm.step)"
                    :placeholder="dimensionPlaceholder(product.dimensionRanges.depthMm.default)"
                    @focus="enableCustomDimensions"
                    @input="updateCustomDimension('depthMm', $event)"
                  />
                  <small>
                    范围 {{ millimetersToCentimeters(product.dimensionRanges.depthMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.depthMm.max) }} cm，
                    每次增减 {{ millimetersToCentimeters(product.dimensionRanges.depthMm.step) }} cm
                  </small>
                </label>
              </div>
            </div>
          </fieldset>

          <fieldset
            v-if="!hasCustomizationProfile && product.colors.length && selectedSpecificationParts.length"
            class="config-group config-group--part-colors"
          >
            <legend><PaletteIcon :size="20" />选择各部位颜色</legend>
            <p class="part-color-intro">每个部位可分别选择后台已开放的颜色。</p>
            <div class="part-color-groups">
              <div
                v-for="(part, partIndex) in selectedSpecificationParts"
                :key="part.id"
                class="part-color-group"
                role="group"
                :aria-labelledby="`part-color-title-${partIndex}`"
              >
                <div class="part-color-group__heading">
                  <strong :id="`part-color-title-${partIndex}`">{{ part.name }}</strong>
                  <span>请选择该部位颜色</span>
                </div>
                <div v-if="colorsForPart(part).length" class="color-options color-options--part">
                  <label
                    v-for="color in colorsForPart(part)"
                    :key="`${part.id}-${color.code}`"
                    class="color-option"
                  >
                    <input
                      v-model="config.partColors[part.id]"
                      type="radio"
                      :name="`part-color-${partIndex}`"
                      :value="color.code"
                    />
                    <span class="color-option__swatch" :style="{ background: color.swatch }">
                      <img v-if="color.swatchImage" :src="color.swatchImage" :alt="`${color.name}色卡`" />
                    </span>
                    <span>
                      <strong>{{ color.name }}</strong>
                      <small>{{ color.supplierCode }}</small>
                    </span>
                    <CheckCircleIcon :size="20" weight="fill" aria-hidden="true" />
                  </label>
                </div>
                <p v-else class="part-color-empty">该部位暂时没有可选颜色，请联系客服。</p>
              </div>
            </div>
          </fieldset>

          <fieldset v-else-if="!hasCustomizationProfile && product.colors.length" class="config-group">
            <legend><PaletteIcon :size="20" />选择颜色</legend>
            <div class="color-options">
              <label v-for="color in product.colors" :key="color.id" class="color-option">
                <input v-model="config.colorCode" type="radio" name="color" :value="color.code" />
                <span class="color-option__swatch" :style="{ background: color.swatch }">
                  <img v-if="color.swatchImage" :src="color.swatchImage" :alt="`${color.name}色卡`" />
                </span>
                <span>
                  <strong>{{ color.name }}</strong>
                  <small>{{ color.supplierCode }}</small>
                </span>
                <CheckCircleIcon :size="20" weight="fill" aria-hidden="true" />
              </label>
            </div>
          </fieldset>

          <fieldset v-if="!hasCustomizationProfile && legacyPurchaseMode && product.customSizeEnabled" class="config-group">
            <legend><RulerIcon :size="20" />定制尺寸（可选）</legend>
            <div class="custom-dimension-mode">
              <p>需要固定标准尺寸之外的尺寸时，可以切换为定制尺寸；规格选择会继续保留。</p>
              <button
                v-if="dimensionMode === 'fixed'"
                class="button button--secondary"
                type="button"
                @click="enableCustomDimensions"
              >
                使用定制尺寸
              </button>
              <button
                v-else
                class="button button--secondary"
                type="button"
                :disabled="!specificationSkus.length"
                @click="returnToFixedSize"
              >
                返回标准尺寸
              </button>
            </div>
            <div v-if="dimensionMode === 'custom'" class="dimension-fields">
              <label>
                <span>{{ product.dimensionRanges.widthMm.label || '宽度' }}（cm／厘米）</span>
                <input
                  :value="millimetersToCentimeters(config.widthMm)"
                  type="number"
                  inputmode="decimal"
                  :min="millimetersToCentimeters(product.dimensionRanges.widthMm.min)"
                  :max="millimetersToCentimeters(product.dimensionRanges.widthMm.max)"
                  :step="millimetersToCentimeters(product.dimensionRanges.widthMm.step)"
                  :placeholder="dimensionPlaceholder(product.dimensionRanges.widthMm.default)"
                  @input="updateCustomDimension('widthMm', $event)"
                />
                <small>
                  范围 {{ millimetersToCentimeters(product.dimensionRanges.widthMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.widthMm.max) }} cm，
                  每次增减 {{ millimetersToCentimeters(product.dimensionRanges.widthMm.step) }} cm
                </small>
              </label>
              <label>
                <span>{{ product.dimensionRanges.heightMm.label || '高度' }}（cm／厘米）</span>
                <input
                  :value="millimetersToCentimeters(config.heightMm)"
                  type="number"
                  inputmode="decimal"
                  :min="millimetersToCentimeters(product.dimensionRanges.heightMm.min)"
                  :max="millimetersToCentimeters(product.dimensionRanges.heightMm.max)"
                  :step="millimetersToCentimeters(product.dimensionRanges.heightMm.step)"
                  :placeholder="dimensionPlaceholder(product.dimensionRanges.heightMm.default)"
                  @input="updateCustomDimension('heightMm', $event)"
                />
                <small>
                  范围 {{ millimetersToCentimeters(product.dimensionRanges.heightMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.heightMm.max) }} cm，
                  每次增减 {{ millimetersToCentimeters(product.dimensionRanges.heightMm.step) }} cm
                </small>
              </label>
              <label>
                <span>{{ product.dimensionRanges.depthMm.label || '深度' }}（cm／厘米）</span>
                <input
                  :value="millimetersToCentimeters(config.depthMm)"
                  type="number"
                  inputmode="decimal"
                  :min="millimetersToCentimeters(product.dimensionRanges.depthMm.min)"
                  :max="millimetersToCentimeters(product.dimensionRanges.depthMm.max)"
                  :step="millimetersToCentimeters(product.dimensionRanges.depthMm.step)"
                  :placeholder="dimensionPlaceholder(product.dimensionRanges.depthMm.default)"
                  @input="updateCustomDimension('depthMm', $event)"
                />
                <small>
                  范围 {{ millimetersToCentimeters(product.dimensionRanges.depthMm.min) }}-{{ millimetersToCentimeters(product.dimensionRanges.depthMm.max) }} cm，
                  每次增减 {{ millimetersToCentimeters(product.dimensionRanges.depthMm.step) }} cm
                </small>
              </label>
            </div>
          </fieldset>

          <fieldset v-if="product.hardwarePackages.length" id="hardware" class="config-group">
            <legend><WrenchIcon :size="20" />{{ hasCustomizationProfile ? '选择五金' : '选择并比较五金方案' }}</legend>
            <p class="hardware-options__intro">默认已包含基础五金，如需升级可在下方对比后选择。</p>
            <div class="hardware-options">
              <label
                v-for="hardware in product.hardwarePackages"
                :key="hardware.id"
                :class="{ 'hardware-option--default': hardware.isDefault }"
              >
                <input
                  v-model="config.hardwareCode"
                  type="radio"
                  name="hardware"
                  :value="hardware.code"
                />
                <span class="hardware-option__body">
                  <span class="hardware-option__heading">
                    <span>
                      <strong>{{ hardware.name }}</strong>
                      <small>{{ hardware.brand }} {{ hardware.model }}</small>
                    </span>
                    <span class="hardware-option__meta">
                      <em>{{ hardware.isDefault ? '默认配置' : '升级可选' }}</em>
                    </span>
                  </span>
                  <p v-if="hardware.description">{{ hardware.description }}</p>
                  <span
                    v-if="selectedHardwareRequirementSummary || hardware.specifications.length"
                    class="hardware-option__spec-count"
                  >
                    {{ selectedHardwareRequirementSummary
                      ? `本规格需 ${selectedHardwareRequirementSummary}`
                      : `已列出 ${hardware.specifications.length} 项组件` }}
                  </span>
                </span>
                <CheckCircleIcon :size="21" weight="fill" aria-hidden="true" />
              </label>
            </div>

            <div v-if="hardwareComparisonRows.length" class="hardware-comparison">
              <div class="hardware-comparison__heading">
                <strong>配件详情对比</strong>
                <span>
                  {{ selectedHardwareRequirementSummary
                    ? `${selectedSku?.name || '当前规格'}：${selectedHardwareRequirementSummary}`
                    : '横向查看每套方案的规格和备注' }}
                </span>
              </div>
              <div
                ref="hardwareComparisonScrollRef"
                class="hardware-comparison__scroll"
                tabindex="0"
                aria-label="五金方案组件横向对比表，可左右滚动"
              >
                <table
                  :style="{
                    minWidth: `${Math.max(560, 112 + product.hardwarePackages.length * 220)}px`,
                  }"
                >
                  <caption>默认与升级五金方案的组件规格和备注对比</caption>
                  <thead>
                    <tr>
                      <th scope="col">五金组件</th>
                      <th
                        v-for="hardware in product.hardwarePackages"
                        :key="hardware.code"
                        scope="col"
                        :class="{ 'hardware-comparison__selected': hardware.code === config.hardwareCode }"
                      >
                        <strong>{{ hardware.name }}</strong>
                        <span>
                          {{ hardware.code === config.hardwareCode
                            ? '当前选择'
                            : hardware.isDefault ? '默认配置' : '升级可选' }}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in hardwareComparisonRows" :key="row.key">
                      <th scope="row">
                        <span>{{ row.label }}</span>
                        <small v-if="row.quantity">×{{ row.quantity }}</small>
                      </th>
                      <td
                        v-for="cell in row.cells"
                        :key="`${row.key}-${cell.code}`"
                        :class="{ 'hardware-comparison__selected': cell.code === config.hardwareCode }"
                      >
                        <strong>{{ cell.value }}</strong>
                        <small v-if="cell.note">{{ cell.note }}</small>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-if="hardwareComparisonScrollable" class="hardware-comparison__hint">
                ← 左右滑动查看全部方案 →
              </p>
            </div>
          </fieldset>

          <div
            v-if="!hasCustomizationProfile || validating || (profileAttempted && validation)"
            class="validation-panel"
            :class="{ 'validation-panel--valid': validation?.valid }"
          >
            <div class="validation-panel__heading">
              <CheckCircleIcon
                v-if="validation?.valid"
                :size="21"
                weight="fill"
                aria-hidden="true"
              />
              <WarningCircleIcon v-else :size="21" weight="fill" aria-hidden="true" />
              <strong>{{ validating ? '正在校验配置' : validation?.valid ? '配置可以提交' : '请修改配置' }}</strong>
            </div>
            <ul v-if="validation?.messages.length">
              <li v-for="message in validation.messages" :key="`${message.field}-${message.message}`">
                {{ message.message }}
              </li>
            </ul>
          </div>

          <div class="config-summary">
            <div>
              <span>当前选择</span>
              <strong>{{ configurationSummary }}</strong>
              <small>提交后客服会联系您，正式报价在量房后给出。</small>
            </div>
            <button
              class="button button--primary button--large"
              type="button"
              :disabled="validating || (!hasCustomizationProfile && (!validation?.valid || !hasRequiredStandardSku))"
              @click="startInquiry"
            >
              {{ hasCustomizationProfile ? '查看您的专属定制' : '提交定制需求' }}
              <ArrowRightIcon :size="19" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <section v-if="product?.parameterGroups.length" class="product-parameters" aria-labelledby="product-parameters-title">
        <div class="product-parameters__heading">
          <h2 id="product-parameters-title">商品参数</h2>
          <p>以下参数供参考，最终以量房后的确认资料为准。</p>
        </div>
        <div class="product-parameter-groups">
          <article v-for="group in product.parameterGroups" :key="group.id">
            <h3>{{ group.name }}</h3>
            <dl>
              <div v-for="item in group.items" :key="item.id">
                <dt>{{ item.label }}</dt>
                <dd>{{ item.value }}</dd>
              </div>
            </dl>
          </article>
        </div>
      </section>
    </div>
  </section>

  <div
    v-if="product && lightboxOpen"
    class="image-lightbox"
    role="dialog"
    aria-modal="true"
    :aria-label="`${product.name}商品大图`"
    @click.self="closeLightbox"
  >
    <button
      ref="lightboxCloseRef"
      class="image-lightbox__close"
      type="button"
      aria-label="关闭大图"
      @click="closeLightbox"
    >
      <CloseIcon :size="24" weight="bold" aria-hidden="true" />
    </button>
    <img
      :src="lightboxImageUrl || activeImageUrl || product.image"
      :alt="lightboxImageAlt || `${product.name}商品大图`"
      :style="{ objectPosition: product.imagePosition }"
      @click.stop
    />
  </div>

  <div v-if="inquiryOpen" class="dialog-backdrop" @click.self="closeInquiry">
    <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="inquiry-title">
      <button class="dialog__close" type="button" aria-label="关闭" @click="closeInquiry">
        <CloseIcon :size="22" weight="bold" aria-hidden="true" />
      </button>
      <template v-if="!submittedNo">
        <div class="dialog__heading">
          <ShieldCheckIcon :size="28" weight="duotone" aria-hidden="true" />
          <div>
            <h2 id="inquiry-title">补充联系信息</h2>
            <p>提交后客服会联系您确认服务范围，不会直接安排生产。</p>
          </div>
        </div>
        <form class="inquiry-form" @submit.prevent="submitInquiry">
          <label>
            <span>联系人</span>
            <input v-model="inquiry.contactName" required autocomplete="name" />
          </label>
          <label>
            <span>微信号</span>
            <input
              v-model="inquiry.wechatId"
              placeholder="方便我们发送报价和效果图"
              autocomplete="off"
            />
          </label>
          <label>
            <span>补充说明（选填）</span>
            <textarea
              v-model="inquiry.notes"
              rows="3"
              placeholder="可以说明预计装修时间、墙面情况或其他疑问"
            ></textarea>
          </label>
          <p v-if="submitError" class="form-error" role="alert">{{ submitError }}</p>
          <button class="button button--primary button--block" type="submit" :disabled="submitting">
            {{ submitting ? '正在提交' : '确认提交询价' }}
          </button>
        </form>
      </template>
      <div v-else class="dialog-success">
        <CheckCircleIcon :size="46" weight="fill" aria-hidden="true" />
        <h2>报价单已收到</h2>
        <p>编号：{{ submittedNo }}</p>
        <span>
          我们会在 24 小时内通过微信发送含效果图的报价。<br />
          如需了解进度可查看订单页面。
        </span>
        <RouterLink class="button button--primary" to="/orders">查看进度</RouterLink>
      </div>
    </section>
  </div>
</template>
